import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, Plus, MessageSquare, Send, Trash2, Pin, Users, X, Clock, Building2, MapPin, Image, EyeOff, Ban, Upload, ChevronDown, ChevronUp, Bell, Search, Map, List, Filter, Globe, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import RadiusSearchMap from '../components/RadiusSearchMap';

const API = process.env.REACT_APP_BACKEND_URL;

const QUICK_REACTIONS = [
  { emoji: '🐝', label: 'Same here' },
  { emoji: '🙏', label: 'Thanks for the heads up' },
  { emoji: '💪', label: 'Stay strong' },
  { emoji: '👀', label: 'Watching this' },
  { emoji: '❤️', label: 'Support' },
];

// Community Guidelines
const COMMUNITY_GUIDELINES = `
🐝 HIVE MIND COMMUNITY GUIDELINES

Welcome to Hive Mind! To maintain a friendly and helpful community:

✅ DO:
• Be respectful and kind to all members
• Share helpful tips and experiences
• Ask questions - we were all beginners once
• Support fellow beekeepers
• Report inappropriate content to admins

❌ DON'T:
• Post offensive, rude, or disrespectful content
• Spam or post irrelevant promotions
• Share misleading or harmful information
• Engage in personal attacks
• Post inappropriate images

⚠️ ENFORCEMENT:
• Admins may hide or delete inappropriate content
• Repeated violations will result in suspension
• We reserve the right to remove any member who violates these guidelines

By posting, you agree to follow these guidelines and help keep our community a safe, friendly place for all beekeepers.
`;

const HiveMind = () => {
  const { token, isAdmin, isDemo, user } = useAuth();
  const navigate = useNavigate();
  const [channels, setChannels] = useState([]);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewPost, setShowNewPost] = useState(false);
  const [newPost, setNewPost] = useState({ title: '', content: '', images: [], includeLocation: false });
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyContent, setReplyContent] = useState('');
  const [showMembers, setShowMembers] = useState(false);
  const [members, setMembers] = useState([]);
  const [accessDenied, setAccessDenied] = useState(false);
  const [showConfirmPost, setShowConfirmPost] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [showManualLocation, setShowManualLocation] = useState(false);
  const [manualLocation, setManualLocation] = useState({ suburb: '', city: '', country: '' });
  
  // Happening section states
  const [expandedSections, setExpandedSections] = useState({});
  const [happeningPosts, setHappeningPosts] = useState({});
  const [happeningTimeFilter, setHappeningTimeFilter] = useState('week');
  const [happeningPostLimit, setHappeningPostLimit] = useState({});
  const [newActivityCounts, setNewActivityCounts] = useState({});
  const [activityTypes, setActivityTypes] = useState([]);
  const [lastVisitTime, setLastVisitTime] = useState(null);
  
  // Area search
  const [areaSearch, setAreaSearch] = useState('');
  const [userLocation, setUserLocation] = useState(null);
  const [locationDetails, setLocationDetails] = useState(null); // Suburb, city, country
  const [fetchingLocation, setFetchingLocation] = useState(false);
  
  // Location filter
  const [locationFilter, setLocationFilter] = useState({ country: '', state: '', city: '' });
  const [availableLocations, setAvailableLocations] = useState({ countries: [], location_tree: {} });
  const [showLocationFilter, setShowLocationFilter] = useState(false);
  const [nearMeActive, setNearMeActive] = useState(false);
  const [nearMeRadius, setNearMeRadius] = useState(50); // Default 50km
  const [nearMeLocation, setNearMeLocation] = useState(null);
  const [fetchingNearMe, setFetchingNearMe] = useState(false);
  
  // Radius search
  const [showMapView, setShowMapView] = useState(false);
  const [radiusSearchPosts, setRadiusSearchPosts] = useState([]);
  const [radiusSearchLoading, setRadiusSearchLoading] = useState(false);
  const [searchCenter, setSearchCenter] = useState(null);
  const [searchRadius, setSearchRadius] = useState(100); // Default 100km radius
  const [selectedActivityFilter, setSelectedActivityFilter] = useState('all');

  // Fetch posts by radius
  const fetchPostsByRadius = useCallback(async (lat, lng, radius, fromDate, toDate) => {
    if (!token) return;
    
    try {
      setRadiusSearchLoading(true);
      const headers = { Authorization: `Bearer ${token}` };
      const params = { lat, lng, radius_km: radius, time_filter: happeningTimeFilter };
      
      // Add date filters if provided
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      
      const res = await axios.get(`${API}/api/hive-mind/channels/happening/posts/by-radius`, {
        params,
        headers
      });
      setRadiusSearchPosts(res.data);
      setSearchCenter([lat, lng]);
      setSearchRadius(radius);
    } catch (err) {
      toast.error('Failed to load posts by location');
    } finally {
      setRadiusSearchLoading(false);
    }
  }, [token]);

  // Auto-search when map view is opened
  useEffect(() => {
    if (showMapView && selectedChannel?.id === 'happening' && token && radiusSearchPosts.length === 0 && !searchCenter) {
      // Try to get user's location first, otherwise use default
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { latitude, longitude } = position.coords;
            fetchPostsByRadius(latitude, longitude, 100); // 100km radius
          },
          () => {
            // Fallback to Melbourne area with larger radius
            fetchPostsByRadius(-37.8136, 144.9631, 150); // 150km to catch surrounding suburbs
          },
          { enableHighAccuracy: false, timeout: 5000, maximumAge: 60000 }
        );
      } else {
        // Fallback to Melbourne area
        fetchPostsByRadius(-37.8136, 144.9631, 150);
      }
    }
  }, [showMapView, selectedChannel, token, radiusSearchPosts.length, searchCenter, fetchPostsByRadius]);

  useEffect(() => {
    // Get last visit time from localStorage
    const lastVisit = localStorage.getItem('hivemind_last_visit');
    if (lastVisit) {
      setLastVisitTime(new Date(lastVisit));
    }
    
    fetchActivityTypes().then((types) => fetchChannels(types));
    
    // Update last visit time
    if (!isDemo) {
      localStorage.setItem('hivemind_last_visit', new Date().toISOString());
    }
  }, [isDemo, navigate]);

  // Fetch activity types from admin config
  const fetchActivityTypes = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/api/config/happening_now`, { headers });
      const types = res.data.items || [];
      setActivityTypes(types);
      
      // Initialize state objects based on fetched activity types
      const initialExpanded = {};
      const initialPosts = {};
      const initialCounts = {};
      types.forEach(t => {
        const key = t.field_name || t.name.toLowerCase().replace(/\s+/g, '_');
        initialExpanded[key] = false;
        initialPosts[key] = [];
        initialCounts[key] = 0;
      });
      setExpandedSections(initialExpanded);
      setHappeningPosts(initialPosts);
      setNewActivityCounts(initialCounts);
      return types;
    } catch (err) {
      // Fallback to default types
      const fallback = [
        { name: 'Harvesting', icon: '🍯', color: 'amber', field_name: 'harvesting' },
        { name: 'Feeding', icon: '🍽️', color: 'blue', field_name: 'feeding' },
        { name: 'Mites Alert', icon: '🐛', color: 'red', field_name: 'mites' },
      ];
      setActivityTypes(fallback);
      return fallback;
    }
  };

  const fetchChannels = async (types) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/api/hive-mind/channels`, { headers });
      setChannels(res.data);
      
      // Check for new activity posts
      await fetchHappeningPosts(headers, null, types);
      
      if (res.data.length > 0 && !selectedChannel) {
        // Select first non-happening channel by default
        const defaultChannel = res.data.find(c => c.id !== 'happening') || res.data[0];
        setSelectedChannel(defaultChannel);
        if (defaultChannel.id !== 'happening') {
          fetchPosts(defaultChannel.id);
        }
      }
      setLoading(false);
    } catch (err) {
      if (err.response?.status === 403) {
        setAccessDenied(true);
      }
      setLoading(false);
    }
  };

  const fetchHappeningPosts = async (headers, timeFilter, typesOverride) => {
    try {
      const filter = timeFilter || happeningTimeFilter;
      const res = await axios.get(`${API}/api/hive-mind/channels/happening/posts?time_filter=${filter}`, { headers });
      const allPosts = res.data;
      
      // Group by activity type using dynamic activity types
      const types = typesOverride || activityTypes;
      const grouped = {};
      types.forEach(t => {
        const key = t.field_name || t.name.toLowerCase().replace(/\s+/g, '_');
        grouped[key] = allPosts.filter(p => p.activity_type === key);
      });
      
      // Also handle any posts that don't match known types (for backwards compatibility)
      const knownKeys = types.map(t => t.field_name || t.name.toLowerCase().replace(/\s+/g, '_'));
      allPosts.forEach(p => {
        if (p.activity_type && !knownKeys.includes(p.activity_type)) {
          if (!grouped[p.activity_type]) grouped[p.activity_type] = [];
          grouped[p.activity_type].push(p);
        }
      });
      
      setHappeningPosts(grouped);
      
      // Count new posts since last visit
      if (lastVisitTime) {
        const counts = {};
        Object.keys(grouped).forEach(key => {
          counts[key] = grouped[key].filter(p => new Date(p.created_at) > lastVisitTime).length;
        });
        setNewActivityCounts(counts);
      }
    } catch (err) {
    }
  };

  const fetchPosts = async (channelId) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/api/hive-mind/channels/${channelId}/posts`, { headers });
      setPosts(res.data);
    } catch (err) {
      toast.error('Failed to load posts');
    }
  };

  const handleTimeFilterChange = (filter) => {
    setHappeningTimeFilter(filter);
    setHappeningPostLimit({});
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    fetchHappeningPosts(headers, filter);
  };

  const handleConfirmActive = async (postId, stillActive) => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : {};
      await axios.put(`${API}/api/hive-mind/posts/${postId}/confirm-active`, { still_active: stillActive }, { headers });
      toast.success(stillActive ? 'Confirmed as still active' : 'Marked as resolved');
      fetchHappeningPosts(headers);
    } catch (err) {
      toast.error('Failed to update');
    }
  };


  const handleChannelSelect = (channel) => {
    setSelectedChannel(channel);
    if (channel.id !== 'happening') {
      fetchPosts(channel.id);
    }
    setShowNewPost(false);
    setReplyingTo(null);
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
    // Clear new count when opened
    if (!expandedSections[section]) {
      setNewActivityCounts(prev => ({
        ...prev,
        [section]: 0
      }));
    }
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (newPost.images.length + files.length > 5) {
      toast.error('Maximum 5 images allowed per post');
      return;
    }

    setUploadingImages(true);
    const uploadedUrls = [];

    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        const headers = { Authorization: `Bearer ${token}` };
        const res = await axios.post(`${API}/api/upload`, formData, { headers });
        uploadedUrls.push(res.data.url);
      } catch (err) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }

    setNewPost(prev => ({ ...prev, images: [...prev.images, ...uploadedUrls] }));
    setUploadingImages(false);
  };

  const removeImage = (index) => {
    setNewPost(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      setFetchingLocation(true);
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          setUserLocation({ lat, lng });
          
          // Reverse geocode to get suburb, city, country
          try {
            const response = await fetch(
              `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&addressdetails=1`
            );
            const data = await response.json();
            const address = data.address || {};
            
            const details = {
              suburb: address.suburb || address.neighbourhood || address.village || address.town || '',
              city: address.city || address.town || address.municipality || address.county || '',
              state: address.state || address.region || '',
              country: address.country || '',
              display: ''
            };
            
            // Create display string: "Suburb, City, Country"
            const parts = [details.suburb, details.city, details.country].filter(Boolean);
            details.display = parts.join(', ');
            
            setLocationDetails(details);
            setShowManualLocation(false);
            toast.success(`Location: ${details.display}`);
          } catch (err) {
            toast.success('Location captured! (Could not get address details)');
          }
          setFetchingLocation(false);
        },
        (error) => {
          setFetchingLocation(false);
          setShowManualLocation(true);
          toast.info('Please enter your location manually');
        },
        { enableHighAccuracy: false, timeout: 10000, maximumAge: 60000 }
      );
    } else {
      setShowManualLocation(true);
      toast.info('Geolocation not supported - please enter manually');
    }
  };

  // Apply manual location
  const applyManualLocation = () => {
    if (!manualLocation.country.trim()) {
      toast.error('Please enter at least a country');
      return;
    }
    
    const details = {
      suburb: manualLocation.suburb.trim(),
      city: manualLocation.city.trim(),
      state: '',
      country: manualLocation.country.trim(),
      display: ''
    };
    
    const parts = [details.suburb, details.city, details.country].filter(Boolean);
    details.display = parts.join(', ');
    
    setLocationDetails(details);
    setShowManualLocation(false);
    toast.success(`Location set: ${details.display}`);
  };

  // Fetch available locations for filtering
  const fetchAvailableLocations = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/api/hive-mind/locations`, { headers });
      setAvailableLocations(res.data);
    } catch (err) {
    }
  };

  useEffect(() => {
    if (token && !isDemo) {
      fetchAvailableLocations();
    }
  }, [token, isDemo]);

  const handleCreatePost = async () => {
    if (!newPost.title.trim() || !newPost.content.trim()) {
      toast.error('Please fill in all fields');
      return;
    }
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const postData = {
        channel_id: selectedChannel.id,
        title: newPost.title,
        content: newPost.content,
        images: newPost.images
      };
      
      // Include location if checkbox checked and location available
      if (newPost.includeLocation && userLocation) {
        postData.gps_lat = userLocation.lat;
        postData.gps_lng = userLocation.lng;
        
        // Include structured location details
        if (locationDetails) {
          postData.location_name = locationDetails.display;
          postData.location_suburb = locationDetails.suburb || '';
          postData.location_city = locationDetails.city || '';
          postData.location_state = locationDetails.state || '';
          postData.location_country = locationDetails.country || '';
        }
      }
      
      await axios.post(`${API}/api/hive-mind/posts`, postData, { headers });
      toast.success('Post created!');
      setNewPost({ title: '', content: '', images: [], includeLocation: false });
      setLocationDetails(null);
      setUserLocation(null);
      setShowNewPost(false);
      setShowConfirmPost(false);
      fetchPosts(selectedChannel.id);
      fetchAvailableLocations(); // Refresh location filters
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create post');
    }
  };

  const handleReply = async (postId) => {
    if (!replyContent.trim()) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/api/hive-mind/posts/${postId}/reply`, {
        content: replyContent
      }, { headers });
      toast.success('Reply added!');
      setReplyContent('');
      setReplyingTo(null);
      if (selectedChannel?.id === 'happening') {
        fetchHappeningPosts({ Authorization: `Bearer ${token}` });
      } else {
        fetchPosts(selectedChannel.id);
      }
    } catch (err) {
      toast.error('Failed to add reply');
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Delete this post?')) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/api/hive-mind/posts/${postId}`, { headers });
      toast.success('Post deleted');
      if (selectedChannel.id === 'happening') {
        fetchHappeningPosts({ Authorization: `Bearer ${token}` });
      } else {
        fetchPosts(selectedChannel.id);
      }
    } catch (err) {
      toast.error('Failed to delete post');
    }
  };

  const handleDeleteReply = async (postId, replyId) => {
    if (!window.confirm('Delete this reply?')) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/api/hive-mind/posts/${postId}/replies/${replyId}`, { headers });
      toast.success('Reply deleted');
      if (selectedChannel?.id === 'happening') {
        fetchHappeningPosts({ Authorization: `Bearer ${token}` });
      } else {
        fetchPosts(selectedChannel.id);
      }
    } catch (err) {
      toast.error('Failed to delete reply');
    }
  };

  const handlePinPost = async (postId) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/api/hive-mind/posts/${postId}/pin`, {}, { headers });
      if (selectedChannel?.id === 'happening') {
        fetchHappeningPosts({ Authorization: `Bearer ${token}` });
      } else {
        fetchPosts(selectedChannel.id);
      }
    } catch (err) {
      toast.error('Failed to pin post');
    }
  };

  const handleReaction = async (postId, emoji) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.post(`${API}/api/hive-mind/posts/${postId}/react`, { emoji }, { headers });
      // Update the post's reactions in local state
      const updateReactions = (posts) =>
        posts.map(p => p.id === postId ? { ...p, reactions: res.data.reactions } : p);
      setPosts(prev => updateReactions(prev));
      // Also update happening posts
      setHappeningPosts(prev => {
        const updated = { ...prev };
        Object.keys(updated).forEach(key => {
          updated[key] = updateReactions(updated[key]);
        });
        return updated;
      });
    } catch (err) {
      toast.error('Failed to react');
    }
  };

  const handleHidePost = async (postId) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/api/hive-mind/posts/${postId}/hide`, {}, { headers });
      toast.success('Post visibility toggled');
      if (selectedChannel.id === 'happening') {
        fetchHappeningPosts({ Authorization: `Bearer ${token}` });
      } else {
        fetchPosts(selectedChannel.id);
      }
    } catch (err) {
      toast.error('Failed to hide post');
    }
  };

  const fetchMembers = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/api/hive-mind/members`, { headers });
      setMembers(res.data);
      setShowMembers(true);
    } catch (err) {
      toast.error('Failed to load members');
    }
  };

  const handleRemoveMember = async (userId) => {
    if (!window.confirm('Remove this user from Hive Mind?')) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/api/hive-mind/members/${userId}/remove`, {}, { headers });
      toast.success('User removed from Hive Mind');
      fetchMembers();
    } catch (err) {
      toast.error('Failed to remove user');
    }
  };

  const handleBanMember = async (userId, userName) => {
    if (!window.confirm(`Ban ${userName} and hide all their posts?`)) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.put(`${API}/api/hive-mind/members/${userId}/ban`, {}, { headers });
      toast.success(res.data.message);
      fetchMembers();
      if (selectedChannel) {
        if (selectedChannel.id === 'happening') {
          fetchHappeningPosts({ Authorization: `Bearer ${token}` });
        } else {
          fetchPosts(selectedChannel.id);
        }
      }
    } catch (err) {
      toast.error('Failed to ban user');
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Filter posts by area search (suburb) and location filters
  const filterPostsByArea = (postsToFilter) => {
    let filtered = postsToFilter;
    
    // Apply text search
    if (areaSearch.trim()) {
      filtered = filtered.filter(p => 
        p.location_name?.toLowerCase().includes(areaSearch.toLowerCase()) ||
        p.location_suburb?.toLowerCase().includes(areaSearch.toLowerCase()) ||
        p.location_city?.toLowerCase().includes(areaSearch.toLowerCase())
      );
    }
    
    // Apply location filters
    if (locationFilter.country) {
      filtered = filtered.filter(p => p.location_country === locationFilter.country);
    }
    if (locationFilter.state) {
      filtered = filtered.filter(p => p.location_state === locationFilter.state);
    }
    if (locationFilter.city) {
      filtered = filtered.filter(p => p.location_city === locationFilter.city);
    }
    
    return filtered;
  };

  // Filter regular channel posts
  const filterPosts = (postsToFilter) => {
    let filtered = postsToFilter;
    
    // Apply Near Me filter first (takes priority)
    if (nearMeActive && nearMeLocation) {
      filtered = filterByNearMe(filtered);
      return filtered;
    }
    
    // Apply location filters
    if (locationFilter.country) {
      filtered = filtered.filter(p => p.location_country === locationFilter.country);
    }
    if (locationFilter.state) {
      filtered = filtered.filter(p => p.location_state === locationFilter.state);
    }
    if (locationFilter.city) {
      filtered = filtered.filter(p => p.location_city === locationFilter.city);
    }
    
    return filtered;
  };

  // Clear location filter
  const clearLocationFilter = () => {
    setLocationFilter({ country: '', state: '', city: '' });
    setNearMeActive(false);
    setNearMeLocation(null);
  };

  // Calculate distance between two GPS coordinates (Haversine formula)
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // Earth's radius in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c; // Distance in km
  };

  // Activate Near Me filter
  const activateNearMe = () => {
    if (navigator.geolocation) {
      setFetchingNearMe(true);
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const loc = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          setNearMeLocation(loc);
          setNearMeActive(true);
          setLocationFilter({ country: '', state: '', city: '' }); // Clear country filter
          setFetchingNearMe(false);
          toast.success(`Showing posts within ${nearMeRadius}km of you`);
        },
        (error) => {
          toast.error('Could not get your location for Near Me filter');
          setFetchingNearMe(false);
        },
        { enableHighAccuracy: false, timeout: 10000, maximumAge: 60000 }
      );
    } else {
      toast.error('Geolocation not supported');
    }
  };

  // Filter posts by Near Me (within radius)
  const filterByNearMe = (postsToFilter) => {
    if (!nearMeActive || !nearMeLocation) return postsToFilter;
    
    return postsToFilter.filter(post => {
      if (!post.gps_lat || !post.gps_lng) return false;
      const distance = calculateDistance(
        nearMeLocation.lat, nearMeLocation.lng,
        post.gps_lat, post.gps_lng
      );
      return distance <= nearMeRadius;
    });
  };

  // Get available states for selected country
  const getAvailableStates = () => {
    if (!locationFilter.country || !availableLocations.location_tree[locationFilter.country]) return [];
    return Object.keys(availableLocations.location_tree[locationFilter.country].states || {}).sort();
  };

  // Get available cities for selected state
  const getAvailableCities = () => {
    if (!locationFilter.country || !locationFilter.state) return [];
    const tree = availableLocations.location_tree[locationFilter.country];
    if (!tree?.states?.[locationFilter.state]) return [];
    return Object.keys(tree.states[locationFilter.state].cities || {}).sort();
  };

  // Render a single post
  const renderPost = (post, showActivityBadge = false) => {
    // Build location display
    const getLocationDisplay = () => {
      if (post.location_suburb && post.location_country) {
        return `${post.location_suburb}, ${post.location_country}`;
      }
      if (post.location_city && post.location_country) {
        return `${post.location_city}, ${post.location_country}`;
      }
      if (post.location_country) {
        return post.location_country;
      }
      if (post.location_name) {
        return post.location_name.split(',')[0];
      }
      return null;
    };

    const locationDisplay = getLocationDisplay();

    return (
      <div key={post.id} className={`bg-white rounded-xl shadow-sm overflow-hidden mb-3 ${post.pinned ? 'border-2 border-amber-400' : 'border border-stone-200'} ${post.hidden ? 'opacity-50 border-2 border-red-300' : ''}`}>
        {post.pinned && (
          <div className="bg-amber-100 px-3 py-1 text-xs font-medium text-amber-800 flex items-center gap-1">
            <Pin className="w-3 h-3" /> Pinned
          </div>
        )}
        {post.hidden && isAdmin && (
          <div className="bg-red-100 px-3 py-1 text-xs font-medium text-red-800 flex items-center gap-1">
            <EyeOff className="w-3 h-3" /> Hidden
          </div>
        )}
        <div className="p-4">
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <h4 className="font-bold text-stone-800">{post.title}</h4>
              <div className="flex items-center gap-2 text-xs text-stone-500 mt-1 flex-wrap">
                <span className="font-medium text-stone-700">{post.user_name}</span>
                {locationDisplay && (
                  <span className="flex items-center gap-1 text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">
                    <Globe className="w-3 h-3" />
                    {locationDisplay}
                  </span>
                )}
                <span className="text-stone-400">{formatDate(post.created_at)}</span>
              </div>
            </div>
            <div className="flex gap-1">
              {isAdmin && (
                <>
                  <button onClick={() => handlePinPost(post.id)} className={`p-1.5 rounded ${post.pinned ? 'bg-amber-100 text-amber-700' : 'hover:bg-stone-100 text-stone-400'}`}>
                    <Pin className="w-3 h-3" />
                  </button>
                  <button onClick={() => handleHidePost(post.id)} className={`p-1.5 rounded ${post.hidden ? 'bg-red-100 text-red-700' : 'hover:bg-stone-100 text-stone-400'}`}>
                    <EyeOff className="w-3 h-3" />
                  </button>
                </>
              )}
              {(post.user_id === user?.id || isAdmin) && (
                <button onClick={() => handleDeletePost(post.id)} className="p-1.5 hover:bg-red-50 text-red-500 rounded">
                  <Trash2 className="w-3 h-3" />
                </button>
              )}
            </div>
          </div>
          <p className="text-stone-700 mt-2 text-sm whitespace-pre-wrap">{post.content}</p>
        
        {/* Post Images */}
        {post.images && post.images.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {post.images.map((img, idx) => (
              <img 
                key={idx} 
                src={img.startsWith('http') ? img : `${API}${img}`} 
                alt="" 
                className="w-24 h-24 object-cover rounded-lg cursor-pointer hover:opacity-90"
                onClick={() => window.open(img.startsWith('http') ? img : `${API}${img}`, '_blank')}
              />
            ))}
          </div>
        )}
        
        {/* Quick Reactions */}
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          {QUICK_REACTIONS.map(r => {
            const reacted = (post.reactions?.[r.emoji] || []).some(u => u.user_id === user?.id);
            const count = (post.reactions?.[r.emoji] || []).length;
            return (
              <button
                key={r.emoji}
                onClick={() => handleReaction(post.id, r.emoji)}
                title={r.label + (count > 0 ? ` (${(post.reactions?.[r.emoji] || []).map(u => u.user_name).join(', ')})` : '')}
                className={`flex items-center gap-1 text-xs px-2 py-1 rounded-full border transition ${
                  reacted
                    ? 'bg-amber-100 border-amber-300 text-amber-800'
                    : 'bg-white border-stone-200 text-stone-500 hover:bg-stone-50 hover:border-stone-300'
                }`}
                data-testid={`reaction-${r.emoji}-${post.id}`}
              >
                <span>{r.emoji}</span>
                {count > 0 && <span className="font-semibold">{count}</span>}
              </button>
            );
          })}
        </div>

        {/* Replies */}
        {post.replies && post.replies.length > 0 && (
          <div className="mt-3 pt-3 border-t border-stone-100 space-y-2">
            {post.replies.map(reply => (
              <div key={reply.id} className="bg-stone-50 rounded-lg p-2 text-sm">
                <div className="flex justify-between items-center">
                  <span className="font-medium text-stone-700">{reply.user_name}</span>
                  {(reply.user_id === user?.id || isAdmin) && (
                    <button onClick={() => handleDeleteReply(post.id, reply.id)} className="p-1 hover:bg-red-100 text-red-500 rounded">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  )}
                </div>
                <p className="text-stone-600">{reply.content}</p>
              </div>
            ))}
          </div>
        )}
        
        {/* Reply button */}
        {replyingTo === post.id ? (
          <div className="mt-3 flex gap-2">
            <Input
              placeholder="Write a reply..."
              value={replyContent}
              onChange={e => setReplyContent(e.target.value)}
              className="flex-1 h-9 text-sm"
              onKeyPress={e => e.key === 'Enter' && handleReply(post.id)}
            />
            <Button onClick={() => handleReply(post.id)} size="sm" className="bg-amber-600 hover:bg-amber-700">
              <Send className="w-3 h-3" />
            </Button>
            <Button variant="outline" size="sm" onClick={() => { setReplyingTo(null); setReplyContent(''); }}>
              Cancel
            </Button>
          </div>
        ) : (
          <button onClick={() => setReplyingTo(post.id)} className="mt-2 text-xs text-amber-600 hover:text-amber-700 font-medium flex items-center gap-1">
            <MessageSquare className="w-3 h-3" /> Reply
          </button>
        )}
      </div>
    </div>
  );
  };

  // Render collapsible section for Happening channel
  const renderHappeningSection = (type, title, icon, bgColor, posts) => {
    const filteredPosts = filterPostsByArea(posts);
    const activePosts = filteredPosts.filter(p => !p.resolved && !p.archived);
    const isExpanded = expandedSections[type];
    const newCount = newActivityCounts[type];
    const limit = happeningPostLimit[type] || 5;
    const visiblePosts = filteredPosts.slice(0, limit);
    const hasMore = filteredPosts.length > limit;
    
    return (
      <div className="mb-4">
        <button
          onClick={() => toggleSection(type)}
          className={`w-full flex items-center justify-between p-4 rounded-xl ${bgColor} transition-all hover:opacity-90`}
        >
          <div className="flex items-center gap-3">
            <span className="text-2xl">{icon}</span>
            <div className="text-left">
              <h3 className="font-bold text-stone-800">{title}</h3>
              <p className="text-xs text-stone-600">
                {activePosts.length > 0 ? (
                  <span className="text-emerald-700 font-semibold">{activePosts.length} active</span>
                ) : (
                  <span>{filteredPosts.length} posts</span>
                )}
                {filteredPosts.length > activePosts.length && activePosts.length > 0 && (
                  <span className="text-stone-400 ml-1">· {filteredPosts.length - activePosts.length} resolved</span>
                )}
              </p>
            </div>
            {newCount > 0 && (
              <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
                <Bell className="w-3 h-3" /> {newCount} new
              </span>
            )}
          </div>
          {isExpanded ? <ChevronUp className="w-5 h-5 text-stone-600" /> : <ChevronDown className="w-5 h-5 text-stone-600" />}
        </button>
        
        {isExpanded && (
          <div className="mt-3 pl-4 border-l-4 border-stone-200 space-y-2">
            {visiblePosts.length === 0 ? (
              <p className="text-stone-500 text-sm py-4">No {title.toLowerCase()} activity {happeningTimeFilter === 'week' ? 'this week' : happeningTimeFilter === 'month' ? 'this month' : 'in archive'}</p>
            ) : (
              visiblePosts.map(post => (
                <div key={post.id}>
                  {/* Age, location & resolved badge */}
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    {post.resolved && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium">
                        Resolved
                      </span>
                    )}
                    {post.age_days !== undefined && (
                      <span className={`text-xs px-2 py-0.5 rounded-full ${post.resolved ? 'bg-stone-100 text-stone-400' : post.age_days <= 1 ? 'bg-green-100 text-green-700' : post.age_days <= 3 ? 'bg-amber-100 text-amber-700' : 'bg-stone-100 text-stone-500'}`}>
                        {post.age_days === 0 ? 'Today' : post.age_days === 1 ? 'Yesterday' : `${post.age_days}d ago`}
                      </span>
                    )}
                    {(post.city || post.region || post.location_text) && (
                      <span className="text-xs text-stone-400">{post.city || post.region || post.location_text}</span>
                    )}
                  </div>
                  {renderPost(post, true)}
                  {/* "Still happening?" prompt for author's own posts after 3 days */}
                  {post.needs_confirmation && post.user_id === (user?.id || localStorage.getItem('userId')) && (
                    <div className="ml-4 mt-1 mb-2 bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center justify-between">
                      <span className="text-sm text-amber-700">Is this still happening?</span>
                      <div className="flex gap-2">
                        <button onClick={() => handleConfirmActive(post.id, true)} className="text-xs bg-green-500 text-white px-3 py-1 rounded-full font-medium hover:bg-green-600">
                          Yes, still active
                        </button>
                        <button onClick={() => handleConfirmActive(post.id, false)} className="text-xs bg-stone-300 text-stone-700 px-3 py-1 rounded-full font-medium hover:bg-stone-400">
                          Resolved
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
            {hasMore && (
              <button
                onClick={() => setHappeningPostLimit(prev => ({ ...prev, [type]: limit + 10 }))}
                className="text-sm text-amber-600 hover:text-amber-700 font-medium py-2"
              >
                Load more ({filteredPosts.length - limit} remaining)
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="bg-stone-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  if (accessDenied) {
    return (
      <div className="bg-stone-100 p-8">
        <div className="max-w-xl mx-auto bg-white rounded-2xl shadow-lg p-8 text-center">
          <div className="text-6xl mb-4">🔒</div>
          <h1 className="text-2xl font-bold text-stone-800 mb-2">Hive Mind Access Required</h1>
          <p className="text-stone-600 mb-6">Enable Hive Mind access in your profile to join.</p>
          <div className="flex gap-3 justify-center">
            <Button variant="outline" onClick={() => navigate('/')}>
              <ArrowLeft className="w-4 h-4 mr-2" /> Dashboard
            </Button>
            <Button onClick={() => navigate('/my-profile')} className="bg-amber-600 hover:bg-amber-700">
              Go to Profile
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const showLocationCheckbox = selectedChannel && (selectedChannel.id === 'harvesting' || selectedChannel.id === 'pest-control');

  return (
    <div>
      {/* Header */}
      <div className="p-4">
        <div className="max-w-7xl">
          <div className="flex items-center justify-between gap-2">
            <div>
              <h1 className="text-2xl font-bold text-stone-800 flex items-center gap-2">
                <MessageSquare className="h-6 w-6 text-amber-600" />
                Hive Mind
              </h1>
              <p className="text-sm text-stone-500 mt-1">Community Forum</p>
            </div>
            {isAdmin && (
              <Button 
                onClick={fetchMembers} 
                variant="outline" 
                className="flex-shrink-0 text-xs sm:text-sm px-2 sm:px-4 h-9 sm:h-10"
              >
                <Users className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Manage Members</span>
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl p-4 flex flex-col lg:flex-row gap-4">
        {/* Channels Sidebar - Horizontal scroll on mobile, vertical on desktop */}
        <div className="w-full lg:w-64 lg:flex-shrink-0">
          <div className="bg-white rounded-2xl shadow-lg p-4">
            <h2 className="font-bold text-stone-800 mb-3 hidden lg:block">Channels</h2>
            
            {/* Mobile: Horizontal scrollable row */}
            <div className="lg:hidden">
              <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1 scrollbar-hide">
                {channels.filter(c => c.id !== 'happening').map(channel => (
                  <button
                    key={channel.id}
                    onClick={() => handleChannelSelect(channel)}
                    className={`flex-shrink-0 px-3 py-2 rounded-xl transition-all text-sm whitespace-nowrap ${
                      selectedChannel?.id === channel.id
                        ? 'bg-amber-100 text-amber-900 font-medium'
                        : 'bg-stone-100 hover:bg-stone-200 text-stone-700'
                    }`}
                  >
                    <span className="mr-1">{channel.icon}</span>
                    {channel.name}
                  </button>
                ))}
                <button
                  onClick={() => handleChannelSelect({ id: 'happening', name: 'Happening in the Apiary', icon: '📍' })}
                  className={`flex-shrink-0 px-3 py-2 rounded-xl transition-all text-sm whitespace-nowrap ${
                    selectedChannel?.id === 'happening'
                      ? 'bg-blue-100 text-blue-900 font-medium'
                      : 'bg-blue-50 hover:bg-blue-100 text-blue-700'
                  }`}
                >
                  <span className="mr-1">📍</span>
                  Happening Now
                  {Object.values(newActivityCounts).reduce((sum, c) => sum + c, 0) > 0 && (
                    <span className="ml-1 bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                      {Object.values(newActivityCounts).reduce((sum, c) => sum + c, 0)}
                    </span>
                  )}
                </button>
              </div>
            </div>
            
            {/* Desktop: Vertical list */}
            <div className="hidden lg:block space-y-1">
              {channels.filter(c => c.id !== 'happening').map(channel => (
                <button
                  key={channel.id}
                  onClick={() => handleChannelSelect(channel)}
                  className={`w-full text-left p-3 rounded-xl transition-all ${
                    selectedChannel?.id === channel.id
                      ? 'bg-amber-100 text-amber-900 font-medium'
                      : 'hover:bg-stone-100 text-stone-700'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span>{channel.icon}</span>
                    <span className="text-sm">{channel.name}</span>
                  </div>
                  <div className="text-xs text-stone-500 mt-1 ml-6">
                    {channel.post_count || 0} posts
                  </div>
                </button>
              ))}
              
              {/* Happening in the Apiary - Special Section */}
              <div className="mt-4 pt-4 border-t border-stone-200">
                <button
                  onClick={() => handleChannelSelect({ id: 'happening', name: 'Happening in the Apiary', icon: '📍' })}
                  className={`w-full text-left p-3 rounded-xl transition-all ${
                    selectedChannel?.id === 'happening'
                      ? 'bg-blue-100 text-blue-900 font-medium'
                      : 'hover:bg-blue-50 text-blue-700 bg-blue-50/50'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span>📍</span>
                    <span className="text-sm font-medium">Happening Now</span>
                    {Object.values(newActivityCounts).reduce((sum, c) => sum + c, 0) > 0 && (
                      <span className="bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                        {Object.values(newActivityCounts).reduce((sum, c) => sum + c, 0)}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-blue-600 mt-1 ml-6">
                    Live activity feed
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1">
          {selectedChannel && selectedChannel.id === 'happening' ? (
            // Happening Channel - Collapsible Sections
            <div>
              <div className="bg-white rounded-2xl shadow-lg p-4 mb-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <h2 className="text-xl font-bold text-stone-800 flex items-center gap-2">
                      📍 Happening in the Apiary
                    </h2>
                    <p className="text-sm text-stone-500 mt-1">Live activity updates from beekeepers in your area</p>
                  </div>
                  
                  {/* View Toggle */}
                  <div className="flex items-center gap-1 bg-stone-100 rounded-lg p-1">
                    <button
                      onClick={() => setShowMapView(false)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                        !showMapView 
                          ? 'bg-white text-stone-800 shadow-sm' 
                          : 'text-stone-500 hover:text-stone-700'
                      }`}
                    >
                      <List className="w-4 h-4" />
                      List
                    </button>
                    <button
                      onClick={() => setShowMapView(true)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                        showMapView 
                          ? 'bg-white text-purple-700 shadow-sm' 
                          : 'text-stone-500 hover:text-stone-700'
                      }`}
                    >
                      <Map className="w-4 h-4" />
                      Map
                    </button>
                  </div>
                </div>
                
                {/* Search - Different for each view */}
                {!showMapView && (
                  <div className="mt-4 relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-stone-400" />
                    <Input
                      placeholder="Search by suburb..."
                      value={areaSearch}
                      onChange={(e) => setAreaSearch(e.target.value)}
                      className="pl-10 h-10"
                    />
                  </div>
                )}
              </div>
              
              {/* Map View with Radius Search */}
              {showMapView ? (
                <div className="mb-4">
                  {/* Activity Type Filters */}
                  <div className="bg-white rounded-xl p-3 mb-3 shadow-sm">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs font-medium text-stone-600">Filter by activity:</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => setSelectedActivityFilter('all')}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                          selectedActivityFilter === 'all'
                            ? 'bg-purple-600 text-white'
                            : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                        }`}
                      >
                        🌍 All Activities
                      </button>
                      {activityTypes.filter(t => t.is_active !== false).map(activity => {
                        const key = activity.field_name || activity.name.toLowerCase().replace(/\s+/g, '_');
                        const colorMap = {
                          amber: { active: 'bg-amber-500 text-white', inactive: 'bg-amber-100 text-amber-700' },
                          blue: { active: 'bg-blue-500 text-white', inactive: 'bg-blue-100 text-blue-700' },
                          red: { active: 'bg-red-500 text-white', inactive: 'bg-red-100 text-red-700' },
                          green: { active: 'bg-green-500 text-white', inactive: 'bg-green-100 text-green-700' },
                          teal: { active: 'bg-teal-500 text-white', inactive: 'bg-teal-100 text-teal-700' },
                          pink: { active: 'bg-pink-500 text-white', inactive: 'bg-pink-100 text-pink-700' },
                          purple: { active: 'bg-purple-500 text-white', inactive: 'bg-purple-100 text-purple-700' },
                        };
                        const colors = colorMap[activity.color] || colorMap.purple;
                        return (
                          <button
                            key={key}
                            onClick={() => setSelectedActivityFilter(key)}
                            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                              selectedActivityFilter === key ? colors.active : colors.inactive
                            }`}
                          >
                            {activity.icon || '📍'} {activity.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <RadiusSearchMap
                    posts={selectedActivityFilter === 'all' 
                      ? radiusSearchPosts 
                      : radiusSearchPosts.filter(p => p.activity_type === selectedActivityFilter)}
                    onRadiusSearch={fetchPostsByRadius}
                    initialCenter={searchCenter}
                    initialRadius={searchRadius}
                    activityTypes={activityTypes}
                  />
                  
                  {/* Results List */}
                  {radiusSearchLoading ? (
                    <div className="bg-white rounded-xl p-6 mt-4 text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
                      <p className="text-stone-500 mt-2 text-sm">Searching area...</p>
                    </div>
                  ) : (selectedActivityFilter === 'all' ? radiusSearchPosts : radiusSearchPosts.filter(p => p.activity_type === selectedActivityFilter)).length > 0 ? (
                    <div className="mt-4 space-y-3">
                      <h3 className="font-semibold text-stone-700 text-sm flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-purple-600" />
                        {(selectedActivityFilter === 'all' ? radiusSearchPosts : radiusSearchPosts.filter(p => p.activity_type === selectedActivityFilter)).length} activities within {searchRadius}km
                        {selectedActivityFilter !== 'all' && (
                          <span className="text-xs text-stone-400">
                            (filtered: {activityTypes.find(t => (t.field_name || t.name.toLowerCase().replace(/\s+/g, '_')) === selectedActivityFilter)?.name || selectedActivityFilter})
                          </span>
                        )}
                      </h3>
                      {(selectedActivityFilter === 'all' ? radiusSearchPosts : radiusSearchPosts.filter(p => p.activity_type === selectedActivityFilter)).map(post => {
                        const activityConfig = activityTypes.find(t => (t.field_name || t.name.toLowerCase().replace(/\s+/g, '_')) === post.activity_type);
                        const colorMap = {
                          amber: 'bg-amber-100 text-amber-700',
                          blue: 'bg-blue-100 text-blue-700',
                          red: 'bg-red-100 text-red-700',
                          green: 'bg-green-100 text-green-700',
                          teal: 'bg-teal-100 text-teal-700',
                          pink: 'bg-pink-100 text-pink-700',
                          purple: 'bg-purple-100 text-purple-700',
                        };
                        const badgeColor = colorMap[activityConfig?.color] || 'bg-purple-100 text-purple-700';
                        return (
                          <div key={post.id} className="bg-white rounded-xl border border-stone-200 p-4">
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${badgeColor}`}>
                                    {activityConfig?.icon || '📍'} {activityConfig?.name || post.activity_type}
                                  </span>
                                  {post.distance_km !== undefined && (
                                    <span className="text-xs text-purple-600 bg-purple-50 px-2 py-0.5 rounded-full">
                                      📏 {post.distance_km} km
                                    </span>
                                  )}
                                </div>
                                <p className="font-medium text-stone-800 mt-2">{post.user_name}</p>
                                {post.location_name && (
                                  <p className="text-xs text-stone-500 flex items-center gap-1 mt-1">
                                    <MapPin className="w-3 h-3" />
                                    {post.location_name}
                                  </p>
                                )}
                                <p className="text-sm text-stone-600 mt-2">{post.content}</p>
                              </div>
                            </div>
                            <p className="text-xs text-stone-400 mt-2">{formatDate(post.created_at)}</p>
                          </div>
                        );
                      })}
                    </div>
                  ) : searchCenter && (
                    <div className="bg-white rounded-xl p-6 mt-4 text-center">
                      <div className="text-4xl mb-2">🔍</div>
                      <p className="text-stone-600">
                        No {selectedActivityFilter !== 'all' ? `"${activityTypes.find(t => (t.field_name || t.name.toLowerCase().replace(/\s+/g, '_')) === selectedActivityFilter)?.name || selectedActivityFilter}"` : ''} activities found within {searchRadius}km
                      </p>
                      <p className="text-sm text-stone-400 mt-1">Try expanding the search radius or changing the activity filter</p>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  {/* Time Filter Tabs */}
                  <div className="flex items-center gap-2 mb-4 bg-white rounded-xl p-1.5 border border-stone-200">
                    {[
                      { id: 'week', label: 'This Week' },
                      { id: 'month', label: 'This Month' },
                      { id: 'archive', label: 'Archive' },
                    ].map(tf => (
                      <button
                        key={tf.id}
                        onClick={() => handleTimeFilterChange(tf.id)}
                        className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          happeningTimeFilter === tf.id
                            ? 'bg-amber-500 text-white'
                            : 'text-stone-600 hover:bg-stone-100'
                        }`}
                      >
                        {tf.label}
                      </button>
                    ))}
                  </div>
                  
                  {activityTypes.filter(t => t.is_active !== false).map(activity => {
                    const key = activity.field_name || activity.name.toLowerCase().replace(/\s+/g, '_');
                    const colorClasses = {
                      amber: 'bg-amber-50 border border-amber-200',
                      blue: 'bg-blue-50 border border-blue-200',
                      red: 'bg-red-50 border border-red-200',
                      green: 'bg-green-50 border border-green-200',
                      purple: 'bg-purple-50 border border-purple-200',
                      pink: 'bg-pink-50 border border-pink-200',
                      orange: 'bg-orange-50 border border-orange-200',
                      teal: 'bg-teal-50 border border-teal-200',
                    };
                    return renderHappeningSection(
                      key,
                      activity.name,
                      activity.icon || '📍',
                      colorClasses[activity.color] || 'bg-stone-50 border border-stone-200',
                      happeningPosts[key] || []
                    );
                  })}
                </>
              )}
            </div>
          ) : selectedChannel && (
            // Regular Channel
            <>
              <div className="bg-white rounded-2xl shadow-lg p-4 mb-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <h2 className="text-lg sm:text-xl font-bold text-stone-800 flex items-center gap-2">
                      {selectedChannel.icon} {selectedChannel.name}
                    </h2>
                    <p className="text-xs sm:text-sm text-stone-500">{selectedChannel.description}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      variant={showLocationFilter ? "default" : "outline"}
                      onClick={() => setShowLocationFilter(!showLocationFilter)} 
                      className={showLocationFilter ? "bg-blue-500 hover:bg-blue-600" : ""}
                      size="sm"
                    >
                      <Filter className="w-4 h-4 mr-1" /> 
                      {nearMeActive ? `Near Me (${nearMeRadius}km)` : locationFilter.country ? `${locationFilter.country}` : 'Filter by Location'}
                    </Button>
                    <Button onClick={() => setShowNewPost(true)} className="bg-amber-600 hover:bg-amber-700">
                      <Plus className="w-4 h-4 mr-2" /> New Post
                    </Button>
                  </div>
                </div>
                
                {/* Location Filter Panel */}
                {showLocationFilter && (
                  <div className="mt-4 pt-4 border-t border-stone-200">
                    {/* Near Me Quick Filter */}
                    <div className="flex flex-wrap items-center gap-3 mb-4">
                      <button
                        onClick={() => {
                          if (nearMeActive) {
                            setNearMeActive(false);
                            setNearMeLocation(null);
                          } else {
                            activateNearMe();
                          }
                        }}
                        disabled={fetchingNearMe}
                        className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                          nearMeActive 
                            ? 'bg-green-500 text-white hover:bg-green-600' 
                            : 'bg-stone-100 text-stone-700 hover:bg-stone-200'
                        }`}
                      >
                        {fetchingNearMe ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <MapPin className="w-4 h-4" />
                        )}
                        {nearMeActive ? 'Near Me ON' : 'Near Me'}
                      </button>
                      
                      {nearMeActive && (
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-stone-600">Radius:</span>
                          <select
                            value={nearMeRadius}
                            onChange={(e) => {
                              setNearMeRadius(Number(e.target.value));
                              toast.success(`Showing posts within ${e.target.value}km`);
                            }}
                            className="px-2 py-1 border border-stone-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-green-500"
                          >
                            <option value={10}>10 km</option>
                            <option value={25}>25 km</option>
                            <option value={50}>50 km</option>
                            <option value={100}>100 km</option>
                            <option value={250}>250 km</option>
                            <option value={500}>500 km</option>
                          </select>
                        </div>
                      )}
                      
                      {!nearMeActive && (
                        <span className="text-stone-400 text-sm">or filter by country:</span>
                      )}
                    </div>
                    
                    {/* Country/State/City Filters (hidden when Near Me is active) */}
                    {!nearMeActive && (
                      <div className="flex flex-wrap items-center gap-3">
                        <Globe className="w-4 h-4 text-blue-500" />
                        <select
                          value={locationFilter.country}
                          onChange={(e) => setLocationFilter({ country: e.target.value, state: '', city: '' })}
                          className="px-3 py-2 border border-stone-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">All Countries</option>
                          {availableLocations.countries.map(country => (
                            <option key={country} value={country}>{country}</option>
                          ))}
                        </select>
                        
                        {locationFilter.country && getAvailableStates().length > 0 && (
                          <select
                            value={locationFilter.state}
                            onChange={(e) => setLocationFilter({ ...locationFilter, state: e.target.value, city: '' })}
                            className="px-3 py-2 border border-stone-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="">All States/Regions</option>
                            {getAvailableStates().map(state => (
                              <option key={state} value={state}>{state}</option>
                            ))}
                          </select>
                        )}
                        
                        {locationFilter.state && getAvailableCities().length > 0 && (
                          <select
                            value={locationFilter.city}
                            onChange={(e) => setLocationFilter({ ...locationFilter, city: e.target.value })}
                            className="px-3 py-2 border border-stone-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="">All Cities</option>
                            {getAvailableCities().map(city => (
                              <option key={city} value={city}>{city}</option>
                            ))}
                          </select>
                        )}
                        
                        {(locationFilter.country || locationFilter.state || locationFilter.city) && (
                          <button
                            onClick={clearLocationFilter}
                            className="text-sm text-red-600 hover:text-red-700 flex items-center gap-1"
                          >
                            <X className="w-4 h-4" /> Clear
                          </button>
                        )}
                      </div>
                    )}
                    
                    {/* Status indicator */}
                    {nearMeActive && nearMeLocation && (
                      <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        Showing posts within <strong>{nearMeRadius}km</strong> of your location
                        <button 
                          onClick={clearLocationFilter}
                          className="ml-2 text-red-500 hover:text-red-600 underline"
                        >
                          Clear
                        </button>
                      </p>
                    )}
                    
                    {locationFilter.country && !nearMeActive && (
                      <p className="text-xs text-stone-500 mt-2">
                        Showing posts from: <span className="font-medium text-stone-700">
                          {[locationFilter.city, locationFilter.state, locationFilter.country].filter(Boolean).join(', ')}
                        </span>
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* New Post Form */}
              {showNewPost && (
                <div className="bg-white rounded-2xl shadow-lg p-4 mb-4">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="font-bold text-stone-800">Create New Post</h3>
                    <button onClick={() => { setShowNewPost(false); setNewPost({ title: '', content: '', images: [], includeLocation: false }); }} className="p-1 hover:bg-stone-100 rounded">
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  <div className="space-y-3">
                    <Input
                      placeholder="Post title..."
                      value={newPost.title}
                      onChange={e => setNewPost({...newPost, title: e.target.value})}
                      className="h-12"
                    />
                    <Textarea
                      placeholder="What's on your mind?"
                      value={newPost.content}
                      onChange={e => setNewPost({...newPost, content: e.target.value})}
                      rows={4}
                    />
                    
                    {/* GPS Location - Available for all channels */}
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl space-y-2">
                      <label className="flex items-start gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={newPost.includeLocation}
                          onChange={e => {
                            setNewPost({...newPost, includeLocation: e.target.checked});
                            if (e.target.checked && !userLocation && !locationDetails) {
                              getCurrentLocation();
                            }
                            if (!e.target.checked) {
                              setShowManualLocation(false);
                              setLocationDetails(null);
                              setManualLocation({ suburb: '', city: '', country: '' });
                            }
                          }}
                          className="w-5 h-5 mt-0.5 rounded border-stone-300 text-blue-600"
                        />
                        <div className="flex-1">
                          <span className="font-medium text-blue-800 flex items-center gap-1">
                            <Globe className="w-4 h-4" /> Include my location
                          </span>
                          <p className="text-xs text-blue-600">Helps others see if advice is relevant to their region</p>
                        </div>
                      </label>
                      
                      {/* Show fetching state or captured location */}
                      {newPost.includeLocation && (
                        <div className="ml-7 space-y-2">
                          {fetchingLocation ? (
                            <div className="flex items-center gap-2 text-sm text-blue-600">
                              <Loader2 className="w-4 h-4 animate-spin" />
                              Getting your location...
                            </div>
                          ) : locationDetails ? (
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 px-2 py-1 rounded">
                                <MapPin className="w-4 h-4" />
                                {locationDetails.display}
                              </div>
                              <button 
                                type="button"
                                onClick={() => {
                                  setLocationDetails(null);
                                  setShowManualLocation(true);
                                }}
                                className="text-xs text-blue-600 hover:text-blue-700 underline"
                              >
                                Change
                              </button>
                            </div>
                          ) : showManualLocation ? (
                            <div className="space-y-2 bg-white p-3 rounded-lg border border-blue-100">
                              <p className="text-sm font-medium text-stone-700">Enter your location:</p>
                              <div className="grid grid-cols-3 gap-2">
                                <Input
                                  placeholder="Suburb (optional)"
                                  value={manualLocation.suburb}
                                  onChange={e => setManualLocation({...manualLocation, suburb: e.target.value})}
                                  className="h-9 text-sm"
                                />
                                <Input
                                  placeholder="City (optional)"
                                  value={manualLocation.city}
                                  onChange={e => setManualLocation({...manualLocation, city: e.target.value})}
                                  className="h-9 text-sm"
                                />
                                <Input
                                  placeholder="Country *"
                                  value={manualLocation.country}
                                  onChange={e => setManualLocation({...manualLocation, country: e.target.value})}
                                  className="h-9 text-sm"
                                />
                              </div>
                              <div className="flex items-center gap-2">
                                <Button 
                                  type="button" 
                                  size="sm" 
                                  onClick={applyManualLocation}
                                  className="bg-blue-500 hover:bg-blue-600 text-xs"
                                >
                                  Set Location
                                </Button>
                                <button 
                                  type="button"
                                  onClick={() => {
                                    setShowManualLocation(false);
                                    getCurrentLocation();
                                  }}
                                  className="text-xs text-blue-600 hover:text-blue-700 underline"
                                >
                                  Try auto-detect again
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center gap-3">
                              <button 
                                type="button"
                                onClick={getCurrentLocation}
                                className="text-sm text-blue-600 hover:text-blue-700 underline"
                              >
                                Auto-detect location
                              </button>
                              <span className="text-stone-400">or</span>
                              <button 
                                type="button"
                                onClick={() => setShowManualLocation(true)}
                                className="text-sm text-blue-600 hover:text-blue-700 underline"
                              >
                                Enter manually
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    
                    {/* Image Upload */}
                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-2">
                        <Image className="w-4 h-4 inline mr-1" /> Add Photos (max 5)
                      </label>
                      <div className="flex flex-wrap gap-2 mb-2">
                        {newPost.images.map((url, idx) => (
                          <div key={idx} className="relative w-20 h-20">
                            <img src={url.startsWith('http') ? url : `${API}${url}`} alt="" className="w-full h-full object-cover rounded-lg" />
                            <button onClick={() => removeImage(idx)} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1">
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                        {newPost.images.length < 5 && (
                          <label className="w-20 h-20 border-2 border-dashed border-stone-300 rounded-lg flex items-center justify-center cursor-pointer hover:border-amber-400">
                            <input type="file" accept="image/*" multiple onChange={handleImageUpload} className="hidden" disabled={uploadingImages} />
                            {uploadingImages ? (
                              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-amber-600"></div>
                            ) : (
                              <Upload className="w-6 h-6 text-stone-400" />
                            )}
                          </label>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex justify-end gap-2">
                      <Button type="button" variant="outline" onClick={() => { 
                        setShowNewPost(false); 
                        setNewPost({ title: '', content: '', images: [], includeLocation: false }); 
                        setShowManualLocation(false);
                        setLocationDetails(null);
                        setManualLocation({ suburb: '', city: '', country: '' });
                      }}>
                        Cancel
                      </Button>
                      <Button onClick={() => setShowConfirmPost(true)} className="bg-amber-600 hover:bg-amber-700">
                        Post
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Posts */}
              <div className="space-y-4">
                {(() => {
                  const filteredPosts = filterPosts(posts);
                  const sortedPosts = filteredPosts.sort((a, b) => (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0));
                  
                  if (posts.length === 0) {
                    return (
                      <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
                        <MessageSquare className="w-12 h-12 text-stone-300 mx-auto mb-3" />
                        <p className="text-stone-500">No posts yet. Be the first to start a discussion!</p>
                      </div>
                    );
                  }
                  
                  if (sortedPosts.length === 0 && nearMeActive) {
                    return (
                      <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
                        <MapPin className="w-12 h-12 text-stone-300 mx-auto mb-3" />
                        <p className="text-stone-500">No posts within {nearMeRadius}km of your location.</p>
                        <p className="text-sm text-stone-400 mt-1">Try increasing the radius or clearing the filter.</p>
                        <div className="flex justify-center gap-3 mt-4">
                          <button 
                            onClick={() => setNearMeRadius(nearMeRadius * 2)}
                            className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                          >
                            Double radius to {nearMeRadius * 2}km
                          </button>
                          <span className="text-stone-300">|</span>
                          <button 
                            onClick={clearLocationFilter}
                            className="text-red-600 hover:text-red-700 text-sm font-medium"
                          >
                            Clear filter
                          </button>
                        </div>
                      </div>
                    );
                  }
                  
                  if (sortedPosts.length === 0 && locationFilter.country) {
                    return (
                      <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
                        <Globe className="w-12 h-12 text-stone-300 mx-auto mb-3" />
                        <p className="text-stone-500">No posts from {locationFilter.city || locationFilter.state || locationFilter.country}.</p>
                        <button 
                          onClick={clearLocationFilter}
                          className="mt-2 text-blue-600 hover:text-blue-700 text-sm font-medium"
                        >
                          Clear filter to see all posts
                        </button>
                      </div>
                    );
                  }
                  
                  return sortedPosts.map(post => renderPost(post));
                })()}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Post Confirmation Modal */}
      {showConfirmPost && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowConfirmPost(false)}>
          <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4" onClick={e => e.stopPropagation()}>
            <h3 className="text-xl font-bold text-stone-800 mb-4">📋 Before You Post</h3>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4 max-h-64 overflow-y-auto text-sm text-stone-700 whitespace-pre-wrap">
              {COMMUNITY_GUIDELINES}
            </div>
            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={() => setShowConfirmPost(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreatePost} className="bg-amber-600 hover:bg-amber-700">
                I Agree - Post Now
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Members Modal (Admin) */}
      {showMembers && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowMembers(false)}>
          <div className="bg-white rounded-2xl p-6 max-w-lg w-full mx-4 max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-stone-800">Hive Mind Members</h3>
              <button onClick={() => setShowMembers(false)} className="p-1 hover:bg-stone-100 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-2">
              {members.map(member => (
                <div key={member.id} className={`flex items-center justify-between p-3 rounded-xl ${member.hive_mind_banned ? 'bg-red-50 border border-red-200' : 'bg-stone-50'}`}>
                  <div>
                    <div className="font-medium text-stone-800 flex items-center gap-2">
                      {member.name || 'No name'}
                      {member.hive_mind_banned && <span className="text-xs bg-red-500 text-white px-2 py-0.5 rounded">BANNED</span>}
                    </div>
                    <div className="text-sm text-stone-500">{member.email}</div>
                  </div>
                  {member.role !== 'admin' && (
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => handleBanMember(member.id, member.name)} className={member.hive_mind_banned ? 'text-green-600 border-green-200' : 'text-orange-600 border-orange-200'}>
                        <Ban className="w-3 h-3 mr-1" />
                        {member.hive_mind_banned ? 'Unban' : 'Ban'}
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleRemoveMember(member.id)} className="text-red-600 border-red-200">
                        Remove
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HiveMind;
